import { ChartBar, Check } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import ELTPipelineIllustration from "@/components/home/ELTPipelineIllustration"
import { BASE_PATH } from "@/constants/app.mjs"
import { productPageLogos } from "@/constants/productPageLogos"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"
import MarketingHeading from "../../../common/MarketingHeading"
import NicerBlockquote from "../../../common/NicerBlockquote"
import ProductPageHeader from "../../../products/ProductPageHeader"
import ProductPageTemplate from "../../../products/ProductPageTemplate"
import {
  CustomerLogoStrip,
  FeatureCard,
  StatsGrid,
  ThreeFeaturesSection,
} from "../../../products/shared"

/** MarketVector index screenshot visualization */
const MethodologyVisualization = () => (
  <div className="relative w-full rounded-xl border border-border-default bg-bg-surface/30 overflow-hidden">
    <img
      src={`/${BASE_PATH}static/images/marketing/products/partners/marketvector.jpg`}
      alt="MarketVector Index powered by Token Terminal"
      className="w-full h-auto object-cover"
    />
    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-bg-default/90 to-transparent p-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-bg-surface border border-border-default flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-fg-secondary">
            <path
              d="M9 12h6M9 16h6M17 21H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div>
          <p className="text-sm font-medium text-fg-default">MarketVector Indexes</p>
          <p className="text-xs text-fg-secondary">Institutional-grade methodology</p>
        </div>
      </div>
    </div>
  </div>
)

/** Methodology whitepaper document - formal documentation style */
const MethodologyWhitepaperVisualization = () => (
  <div
    className="relative border-t border-l border-solid border-border-default rounded-tl-xl bg-bg-default overflow-hidden"
    style={{ maxHeight: "420px", ...FADE_MASK_STYLE }}
  >
    {/* Browser chrome header */}
    <div className="flex items-center gap-2 px-4 py-3 bg-bg-surface/60 border-b border-border-default">
      {/* Window controls */}
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3 rounded-full border border-solid border-border-default" />
        <div className="w-3 h-3 rounded-full border border-solid border-border-default" />
        <div className="w-3 h-3 rounded-full border border-solid border-border-default" />
      </div>
    </div>

    {/* Document content */}
    <div className="p-8">
      {/* Document header */}
      <div className="mb-6">
        <h2 className="text-2xl font-medium text-fg-default mb-4 tracking-tight">
          Circle - Transfer Volume Methodology
        </h2>
        <div className="border-b border-border-default" />
      </div>

      {/* Section 1: Overview */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-fg-default mb-3">1. Overview</h3>
        <p className="text-sm text-fg-secondary leading-relaxed">
          Circle&apos;s Cross-Chain Transfer Protocol (CCTP) transfer volume measures the total
          daily USD value of cross-chain token transfers facilitated by Circle&apos;s CCTP
          infrastructure. This metric captures the volume of tokens burned on source chains as part
          of CCTP&apos;s burn-and-mint cross-chain transfer mechanism. The calculation aggregates{" "}
          <code className="px-1.5 py-0.5 rounded bg-bg-inset text-fg-default text-xs font-mono">
            DepositForBurn
          </code>{" "}
          events across all CCTP-supported blockchains.
        </p>
      </div>

      {/* Section 2: Data sources */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-fg-default mb-3">2. Data sources</h3>
        <p className="text-sm text-fg-secondary mb-3">
          The calculation uses several categories of raw data:
        </p>
        <ul className="space-y-2 text-sm text-fg-secondary">
          <li className="flex gap-2">
            <span className="text-fg-tertiary">•</span>
            <span>
              <strong className="text-fg-default">CCTP burn events:</strong> Parsed{" "}
              <code className="px-1.5 py-0.5 rounded bg-bg-inset text-fg-default text-xs font-mono">
                TokenMessenger_DepositForBurn
              </code>{" "}
              events from EVM-compatible chains including Ethereum, Arbitrum, Avalanche, Base,
              Optimism...
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-fg-tertiary">•</span>
            <span>
              <strong className="text-fg-default">Aptos blockchain events:</strong> Parsed{" "}
              <code className="px-1.5 py-0.5 rounded bg-bg-inset text-fg-default text-xs font-mono">
                TokenMessengerMinter_DepositForBurn
              </code>{" "}
              events from Aptos blockchain
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-fg-tertiary">•</span>
            <span>
              <strong className="text-fg-default">Price feeds:</strong> End-of-day token price feeds
              from Token Terminal&apos;s pricing infrastructure
            </span>
          </li>
        </ul>
      </div>

      {/* Section 3: Step-by-step calculation */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-fg-default mb-3">3. Step-by-step calculation</h3>
        <div className="mb-4">
          <h4 className="text-sm font-medium text-fg-default mb-2">
            Component A: EVM chain CCTP volume extraction
          </h4>
          <ul className="space-y-1 text-sm text-fg-secondary">
            <li className="flex gap-2">
              <span className="text-fg-tertiary">•</span>
              <span>
                <strong className="text-fg-default">Purpose:</strong> Extract and process CCTP burn
                events from EVM-compatible blockchains
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-fg-tertiary">•</span>
              <span>
                <strong className="text-fg-default">Logic:</strong> Parse{" "}
                <code className="px-1.5 py-0.5 rounded bg-bg-inset text-fg-default text-xs font-mono">
                  TokenMessenger_DepositForBurn
                </code>{" "}
                events from multiple EVM chains across both CCTP v1 and v2 implementations...
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
)

/** ELT Pipeline visualization with database stack */
const _DataDeliveryVisualization = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 280 200"
    fill="none"
    className="w-full h-auto"
  >
    {/* Background grid */}
    <defs>
      <pattern id="deliveryGrid" width="20" height="20" patternUnits="userSpaceOnUse">
        <path
          d="M 20 0 L 0 0 0 20"
          fill="none"
          className="stroke-fg-secondary/5"
          strokeWidth="0.5"
        />
      </pattern>
    </defs>
    <rect width="280" height="200" fill="url(#deliveryGrid)" />

    {/* Raw data inputs (left side) - messy/varied */}
    <g transform="translate(15, 35)">
      {/* Raw data block 1 */}
      <rect
        x="0"
        y="0"
        width="50"
        height="35"
        rx="4"
        className="stroke-fg-secondary/30 fill-bg-surface/40"
        strokeWidth="1"
      />
      <line x1="8" y1="10" x2="42" y2="10" className="stroke-fg-secondary/25" strokeWidth="1" />
      <line x1="8" y1="18" x2="35" y2="18" className="stroke-fg-secondary/20" strokeWidth="1" />
      <line x1="8" y1="26" x2="38" y2="26" className="stroke-fg-secondary/15" strokeWidth="1" />

      {/* Raw data block 2 */}
      <rect
        x="0"
        y="45"
        width="50"
        height="35"
        rx="4"
        className="stroke-fg-secondary/30 fill-bg-surface/40"
        strokeWidth="1"
      />
      <line x1="8" y1="55" x2="40" y2="55" className="stroke-fg-secondary/20" strokeWidth="1" />
      <line x1="8" y1="63" x2="30" y2="63" className="stroke-fg-secondary/25" strokeWidth="1" />
      <line x1="8" y1="71" x2="44" y2="71" className="stroke-fg-secondary/15" strokeWidth="1" />

      {/* Raw data block 3 */}
      <rect
        x="0"
        y="90"
        width="50"
        height="35"
        rx="4"
        className="stroke-fg-secondary/30 fill-bg-surface/40"
        strokeWidth="1"
      />
      <line x1="8" y1="100" x2="36" y2="100" className="stroke-fg-secondary/15" strokeWidth="1" />
      <line x1="8" y1="108" x2="42" y2="108" className="stroke-fg-secondary/25" strokeWidth="1" />
      <line x1="8" y1="116" x2="32" y2="116" className="stroke-fg-secondary/20" strokeWidth="1" />
    </g>

    {/* Transformation arrows - converging into pipeline */}
    <g className="stroke-fg-secondary/30" strokeWidth="1.5" strokeDasharray="4 3">
      <path d="M70 52 L87 52 L87 75 L105 75" fill="none" />
      <path d="M70 100 L105 100" fill="none" />
      <path d="M70 148 L87 148 L87 125 L105 125" fill="none" />
    </g>

    {/* Central transformation engine with database stack */}
    <g transform="translate(105, 65)">
      <rect
        x="0"
        y="0"
        width="70"
        height="70"
        rx="8"
        className="stroke-fg-secondary/40 fill-bg-surface/50"
        strokeWidth="1.5"
      />

      {/* Database stack (scaled down from Data Warehouse) */}
      <g
        transform="translate(35, 35) scale(0.32) translate(-50, -85)"
        className="stroke-fg-secondary/50"
        strokeWidth="3"
        fill="none"
      >
        <path d="M4 30 L2 33 L2 64 L4 67 A46 13 0 0 0 96 67 L98 64 L98 33 L96 30" />
        <path d="M4 67 L2 70 L2 100 L4 103 A46 13 0 0 0 96 103 L98 100 L98 70 L96 67" />
        <path d="M4 103 L2 106 L2 137 L4 140 A46 13 0 0 0 96 140 L98 137 L98 106 L96 103" />
        <ellipse cx="50" cy="30" rx="46" ry="13" className="fill-bg-surface" />
      </g>
    </g>

    {/* Output arrows - diverging from pipeline */}
    <g className="stroke-fg-secondary/30" strokeWidth="1.5" strokeDasharray="4 3">
      <path d="M175 75 L192 75 L192 52 L210 52" fill="none" />
      <path d="M175 100 L210 100" fill="none" />
      <path d="M175 125 L192 125 L192 148 L210 148" fill="none" />
    </g>

    {/* Standardized output (right side) - clean/consistent */}
    <g transform="translate(215, 35)">
      {/* Clean data block 1 */}
      <rect
        x="0"
        y="0"
        width="50"
        height="35"
        rx="4"
        className="stroke-fg-secondary/40 fill-bg-surface/50"
        strokeWidth="1.5"
      />
      <rect x="8" y="8" width="34" height="6" rx="2" className="fill-fg-secondary/30" />
      <rect x="8" y="18" width="34" height="6" rx="2" className="fill-fg-secondary/25" />

      {/* Clean data block 2 */}
      <rect
        x="0"
        y="45"
        width="50"
        height="35"
        rx="4"
        className="stroke-fg-secondary/40 fill-bg-surface/50"
        strokeWidth="1.5"
      />
      <rect x="8" y="53" width="34" height="6" rx="2" className="fill-fg-secondary/30" />
      <rect x="8" y="63" width="34" height="6" rx="2" className="fill-fg-secondary/25" />

      {/* Clean data block 3 */}
      <rect
        x="0"
        y="90"
        width="50"
        height="35"
        rx="4"
        className="stroke-fg-secondary/40 fill-bg-surface/50"
        strokeWidth="1.5"
      />
      <rect x="8" y="98" width="34" height="6" rx="2" className="fill-fg-secondary/30" />
      <rect x="8" y="108" width="34" height="6" rx="2" className="fill-fg-secondary/25" />
    </g>
  </svg>
)

const IndexesPage = () => {
  return (
    <ProductPageTemplate
      header={
        <ProductPageHeader
          icon={ChartBar}
          title="Indexes"
          backgroundImageUrl={`/${BASE_PATH}static/images/marketing/products/indexes/banner.png`}
          subtitle="Institutional-grade data for index products and benchmarks"
          ctaButtons={
            <LinkButton size="large" href="/contact" style={{ zIndex: 4 }}>
              Learn more
            </LinkButton>
          }
          whoWeServeText="Index providers, ETF issuers, and benchmark administrators building investable crypto products."
          simpleWhoWeServeText="index providers, ETF issuers, and benchmark administrators"
        />
      }
      productId="indexes"
    >
      {/* Customer Logo Strip */}
      <CustomerLogoStrip
        logos={productPageLogos.indexes}
        title="Powering institutional crypto indexes"
      />

      {/* Three Features Section */}
      <ThreeFeaturesSection
        features={[
          {
            component: (
              <FeatureCard>
                <MethodologyVisualization />
              </FeatureCard>
            ),
            title: "Methodology-grade data quality",
            description:
              "Every metric comes with transparent, documented methodology that meets regulatory standards.",
          },
          {
            component: (
              <FeatureCard className="h-[300px]">
                <MethodologyWhitepaperVisualization />
              </FeatureCard>
            ),
            title: "Full audit trail",
            description:
              "Complete data lineage from raw blockchain to final metric. Every number is reproducible.",
          },
          {
            component: (
              <FeatureCard className="h-[300px] p-0">
                <div className="w-full h-full flex items-center justify-center">
                  <ELTPipelineIllustration />
                </div>
              </FeatureCard>
            ),
            title: "Custom data delivery",
            description:
              "Receive data in the format and frequency your index methodology requires.",
          },
        ]}
      />

      {/* Stats Grid */}
      <div className="mt-32">
        <StatsGrid
          stats={[
            {
              value: "100+",
              label: "Chains",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/ethereum.png`, alt: "Ethereum" },
                { src: `${BASE_PATH}/static/images/marketing/logos/solana.png`, alt: "Solana" },
                { src: `${BASE_PATH}/static/images/marketing/logos/base.png`, alt: "Base" },
                { src: `${BASE_PATH}/static/images/marketing/logos/tron.png`, alt: "Tron" },
              ],
            },
            {
              value: "1,200+",
              label: "Apps",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/aave.png`, alt: "Aave" },
                { src: `${BASE_PATH}/static/images/marketing/logos/uniswap.png`, alt: "Uniswap" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/hyperliquid.png`,
                  alt: "Hyperliquid",
                },
                { src: `${BASE_PATH}/static/images/marketing/logos/sky.png`, alt: "Sky" },
              ],
            },
            {
              value: "7,000+",
              label: "Tokenized assets",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/usdt.png`, alt: "USDT" },
                { src: `${BASE_PATH}/static/images/marketing/logos/usdc.png`, alt: "USDC" },
                { src: `${BASE_PATH}/static/images/marketing/logos/blackrock.png`, alt: "BUIDL" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/robinhood.png`,
                  alt: "Robinhood",
                },
              ],
            },
            {
              value: "2+ PB",
              label: "Of data",
              hasSparkline: true,
            },
          ]}
        />
      </div>

      {/* Customer Quote */}
      <div className="max-w-[900px] mx-auto mt-32">
        <NicerBlockquote
          content="Token Terminal has been instrumental in revolutionizing the way we approach on-chain data for our index business. Their commitment to delivering high-quality, actionable insights has made a significant impact on our joint project to create the first fundamental indexes for crypto."
          author="Martin Leinweber"
          authorTitle="MarketVector Indexes"
          authorImage={`/${BASE_PATH}static/images/marketing/home/testimonials/avatar-mleinweber2.jpg`}
        />
      </div>

      {/* CTA Section */}
      <div className="max-w-[450px] mx-auto mt-72 mb-24">
        <div className="flex flex-col gap-5">
          <MarketingHeading level={2} className="text-2xl">
            Build custom indexes
          </MarketingHeading>
          <div className="flex flex-col gap-3">
            {[
              "Custom metric development",
              "Dedicated support team",
              "White-label solutions",
              "Regulatory compliance support",
            ].map((feature) => (
              <div key={feature} className="flex items-center justify-start gap-2">
                <Icon as={Check} />
                <div className="text-base text-fg-default">{feature}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-start gap-2">
            <LinkButton className="w-full mt-4" size="large" href="/contact" color="success">
              Contact us
            </LinkButton>
          </div>
        </div>
      </div>
    </ProductPageTemplate>
  )
}

export default IndexesPage
